import { Injectable, Inject, forwardRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LocalStorageManagerService } from '../../../utilities/local-storage/local-storage-manager.service';
import { SocketService } from '../socket/socket.service';

// Data
import { UserContactService } from './user-contact/user-contact.service';
import { ChatService } from './chat/chat.service';
import { ChatListService } from './chat-list/chat-list.service';
import { UserGroupService } from './user-group/user-group.service';
import { MessagesService } from './messages/messages.service';
import { NewsMessageService } from './messages/news-message/news-message.service';

import * as _ from 'lodash';
import { UserGroup } from '../../../models/user-group';
import { UserContact } from '../../../models/user-contact';
import { AccountManagerService } from '../account/account-manager.service';
import { TnLoaderService } from '../../../utilities/tn-loader/tn-loader.service';
import { ChatMessageService } from './messages/chat-message/chat-message.service';
import { LoggerService } from '../../../utilities/logger/logger.service';
import { InfoMessageService } from './messages/info-message/info-message.service';
import { TeamnoteConfigService } from '../../../configs/teamnote-config.service';
import { PresenceTypeConstant } from '../../../constants/presence-type.constant';
import { AMQPRoutingKey } from '../../../constants/amqp-routing-key.constant';
import { MessageTypeConstant } from '../../../constants/message-type.constant';
import { TeamNoteLocalStorageKeyConstants } from '../../../constants/local-storage-key.constant';
import { PageUrlConstant } from '../../../constants/page-url.constant';
import { TeamNoteGeneralConstant } from '../../../constants/general.constant';
import { Message } from '../../../models/message';
import { ModuleKeyDefinition } from '../../../constants/module.constant';
import { SideNavService } from '../../../utilities/tn-side-nav/side-nav.service';
import { NewsConstant } from '../../../constants/news.constant';
import { MessageNewsBody } from '../../../models/message-news-body';
import { FileFactoryService } from '../../../utilities/file-factory/file-factory.service';
import { TnDialogService } from '../../../utilities/tn-dialog/tn-dialog.service';
import { NewsModalService } from '../../news/news-modal/news-modal.service';
import {TeamnoteApiService} from '../../../api/teamnote-api.service';
import {WebclientService} from '../../webclient.service';

@Injectable()
export class DataManagerService {

  presenceQueueName: string;
  messageQueueName: string;
  private _isRequestedRecentMessage: boolean = false;
  private _isRequestedRecentNews: boolean = false;
  private _isInitEnd: boolean = false;

  // track previous session
  prevSessionRoute: string = null;

  // track is got missing group under root
  isGotMissingGroupsUnderRoot: boolean = false;

  isGottedRoster: boolean = false;

  isChatLoaded: boolean = false;
  isChatLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  allow_self_message: boolean = true;

  constructor(
    private _localStorageManagerService: LocalStorageManagerService,
    private _socketService: SocketService,
    private _userContactService: UserContactService,
    private _chatService: ChatService,
    private _chatListService: ChatListService,
    private _userGroupService: UserGroupService,
    private _messagesService: MessagesService,
    private _newsMessageService: NewsMessageService,
    private _accountManagerService: AccountManagerService,
    private _tnLoaderService: TnLoaderService,
    private _chatMessageService: ChatMessageService,
    private _loggerService: LoggerService,
    private _infoMessageService: InfoMessageService,
    private _teamnoteConfigService: TeamnoteConfigService,
    private _sideNavService: SideNavService,
    private _teamnoteApiService: TeamnoteApiService,
    // private _fileFactoryService: FileFactoryService,
    // private _tnDialogService: TnDialogService,
    // private _newsModalService: NewsModalService
  ) { 
    this._loggerService.debug("Data manager start initializing...");
    this.resetAllData();
  }

  /**
   * Reset all app data
   * 
   * @memberof DataManagerService
   */
  resetAllData(): void {
    this._loggerService.debug("Resetting app data...");
    this._isRequestedRecentMessage = false;
    this._isRequestedRecentNews = false;
    this._isInitEnd = false;
    this.initAllData();
    this.initRoster();
  }

  /**
   * Init messages
   * 
   * @memberof DataManagerService
   */
  initAllData(): void {
    this._loggerService.debug("Resetting messages...");
    this._messagesService.initMessages();
  }

  /**
   * Init roster
   * - User contact
   * - Chat
   * - User group
   * 
   * @memberof DataManagerService
   */
  initRoster(): void {
    this._loggerService.debug("Resetting roster...");
    this.isGottedRoster = false;
    this._userContactService.initUserContacts();
    this._chatService.initChats();
    this._chatListService.reset();
    this._userGroupService.initUserGroups();
  }

  /**
   * For debugging purpose, gather all useful data and build into object
   * 
   * @returns {*} - Object including all rosters & messages
   * @memberof DataManagerService
   */
  tnDebugger(): any {
    return {
      roster: {
        contacts: this._userContactService.userContacts,
        chats: this._chatService.chats,
        userGroup: this._userGroupService.userGroups
      },
      messages: this._messagesService.messages,
      chatMessages: this._chatMessageService.chatMessages,
      newsMessages: this._newsMessageService.newsMessages,
      infoMessages: {
        read: this._infoMessageService.messageReads,
        delivery: this._infoMessageService.messageDeliveries
      },
      unreads: {
        chatMessages: this._chatMessageService.unreadMessages,
        newsMessages: this._newsMessageService.unreadNews
      }
    };
  }

  /**
   * Base handler for incoming presence
   * 
   * 1. Get roster after queue binding
   * 2. Handle presences by type
   * 
   * @param {any} incomingPresence - Socket incoming message body
   * @returns {void} 
   * @memberof DataManagerService
   */
  receivePresenceCallback(incomingPresence, isBinding?: boolean): void {
    if (isBinding) {
      this._loggerService.debug("Type = queue binding, update presence queue name and get roster");
      this.presenceQueueName = incomingPresence.headers.temp_qname;
      this.getRoster();
      return;  
    }
    let correlationId = incomingPresence.headers['correlation-id'];
    if (!incomingPresence.body) {
      return;
    }
    let presences = JSON.parse(incomingPresence.body);

    let needUpdateContact = false;
    let needUpdateChat = false;
    let needUpdateUserGroup = false;

    _.each(presences, (p) => {
      switch (p.t) {
        case PresenceTypeConstant.WIPE_DEVICE:
          console.error('WIPE');
          this._teamnoteApiService.logoutWebClient(false);
          break;
        case PresenceTypeConstant.USER_CONTACT:
        case PresenceTypeConstant.CONTACT_ABSENCE:
        case PresenceTypeConstant.USER_ONLINE:
        case PresenceTypeConstant.USER_OFFLINE:
          this._userContactService.receiveUserContactPresence(p, correlationId);
          needUpdateContact = true;
          break;
        case PresenceTypeConstant.INDIVIDUAL_CHAT:
        case PresenceTypeConstant.GROUP_CHAT:
        case PresenceTypeConstant.GROUP_BROADCAST:
        case PresenceTypeConstant.CHAT_ABSENCE:
        case PresenceTypeConstant.NEWS_CHANNEL:
        case PresenceTypeConstant.NEWS_CATEGORY:
        case PresenceTypeConstant.MUTE_CHAT:
        case PresenceTypeConstant.MUTE_CHAT_ABSENSE:
          this._chatService.receiveChatPresence(p, correlationId);
          needUpdateChat = true;
          break;
        case PresenceTypeConstant.CHAT_LIST_UPDATE:
          this._chatListService.onChatListUpdatePresence(p);
          break;
        case PresenceTypeConstant.USER_GROUP:
        case PresenceTypeConstant.USER_GROUP_ABSENCE:
          this._userGroupService.receiveUserGroupPresence(p);
          needUpdateUserGroup = true;
          break;
      }
    });

    // If this is a response from GET_ROSTER, do postGetRosterChecking
    if (correlationId == AMQPRoutingKey.CORRELATION_ID.GET_ROSTER) {
      this.postGetRosterChecking();
    }

    // Update roster subject when received corresponding presences, only do update after the whole presence array message is processed
    if (needUpdateContact) {
      this._userContactService.updateContactSubject();
    }
    if (needUpdateChat) {
      this._chatService.updateChatSubject();
    }
    if (needUpdateUserGroup) {
      this._userGroupService.updateUserGroupSubject();
    }
  }

  /**
   * Base handler for incoming messages
   * 
   * Redirect to handler in messageService
   * 
   * @param {any} incomingMessage - Socket incoming message body
   * @returns {void}
   * @memberof DataManagerService
   */
  receiveMessageCallback(incomingMessage, isBinding?: boolean, chatId?: string): void{ 
    if (isBinding) {
      this._loggerService.debug("Type = queue binding, update message queue name");
      this.messageQueueName = incomingMessage.headers.temp_qname;
      this._messagesService._messageQueueName = this.messageQueueName;
      this._newsMessageService._messageQueueName = this.messageQueueName;

      this._loggerService.debug('[messageQueueName in loadOfflineMessage]' + this.messageQueueName);

      // request the offline message
      let disconnectTime = this._localStorageManagerService.getCookiesByKey(TeamNoteLocalStorageKeyConstants.COOKIES.DISCONNECT_TIME);
      if (disconnectTime) {
        this.loadOfflineMessage(disconnectTime);
      }
      return;
    }
    let correlationId = incomingMessage.headers['correlation-id'];

    if (correlationId == AMQPRoutingKey.CORRELATION_ID.LOAD_RECENT_MESSAGE) {
      this._isInitEnd = true;
    }

    if (!incomingMessage.body) {
      return;
    }
    
    this._messagesService.receiveMessages(incomingMessage, chatId);
  }

  /**
   * Get Roster
   * 
   * Roster will be returned in order:
   * 1. user contacts
   * 2. chat
   * 3. user group
   * 4. mute group
   * 
   * @returns {void}
   * @memberof DataManagerService
   */
  getRoster(): void {
    this._loggerService.debug("Start getting roster");
    // No need to get roster if reconnecting to socket
    if (this.isGottedRoster) {
      this._loggerService.debug("Roster is gotten before, no need to get roster");
      this.postGetRosterChecking();
      return;
    }
    this.initRoster();
    let correlationId = AMQPRoutingKey.CORRELATION_ID.GET_ROSTER;
    let routingKey = AMQPRoutingKey[correlationId];
    let headers = {
      'correlation-id': correlationId,
      'reply-to': this.presenceQueueName,
      'device_token': this._localStorageManagerService.getDeviceToken()
    };
    let body = '';

    let callback = (incomingPresence) => {
      this.receivePresenceCallback(incomingPresence);
    };

    this._tnLoaderService.showSpinner('LOADING.LOADING_CONTACTS');
    this._socketService.sendMessage(routingKey, headers, body, correlationId, callback);
  }

  /**
   * Post get roster checking
   * 
   * 1. After news channels received, get recent News
   * 2. After user groups received (end of get roster), get recent messages
   * 3. Restore previous session route
   * 4. Try to get missing user group under root group
   * 
   * @returns {void}
   * @memberof DataManagerService
   */
  postGetRosterChecking(): void {
    this._loggerService.debug("postGetRosterChecking");

    this.isGottedRoster = true;

    // check if user_group info is gotten,
    // if yes, getRecentMessage
    if (this._chatService.getNewsChannels().length > 0) {
      this._loggerService.debug("There are news channels, try to get recent news");
      this.getRecentNews();
    }
    if (_.size(this._userGroupService.userGroups) > 0) {
      this._loggerService.debug("Last chunk of get_roster (user_group) is gotten, try to get recent message");
      this.getRecentMessage();
      this.tryToGetMissingUserGroupUnderRoot();

      /* set flag of loading starred messages to true after After chat is completely loaded */
      // if (!this.isChatLoaded) {
      //   console.log('asdasd');
      //   this.updateIsChatLoaded(true)
      // }
    }

    if (!this.isChatLoaded) {
      // console.log('asdasd');
      this.updateIsChatLoaded(true)
    }

    // Pull server-synced chat lists (favourite/archive) now that chats are available to project
    // membership onto. Debounced: postGetRosterChecking runs once per roster frame, so this
    // coalesces those into a single /chat_list/list call. No-op unless the feature is enabled.
    // Also covers reconnect (getRoster short-circuits here), picking up offline list changes.
    this._chatListService.scheduleReload('roster');

    this.restorePreviousSession();
    this._tnLoaderService.hideSpinner();
  }

  /**
   * Restore last view from previous session
   * 
   * The view's route is stored in cookies, we try to get the previous route nad its meta data in order to show the last view.
   * TODO: check if module is enabled before redirecting. 2018-04-30
   * 
   * @returns {void}
   * @memberof DataManagerService
   */
  restorePreviousSession(): void {
    let prevMetaData = this._localStorageManagerService.getCookiesByKey(TeamNoteLocalStorageKeyConstants.USER_CONFIG_COOKIES.ROUTE_META_DATA);
    if (prevMetaData && this.prevSessionRoute) {
      if (this.prevSessionRoute.indexOf(PageUrlConstant.WEBCLIENT.CHAT) != -1) {
        this._chatService.restoreActiveChatByChatId(prevMetaData);
      } else if (this.prevSessionRoute.indexOf(PageUrlConstant.WEBCLIENT.CONTACT) != -1) {
        this._userContactService.restoreUserGroupIdStack(prevMetaData.split(","));
      }
    }
  }

  /**
   * Get recent News
   * 
   * Only request recent news if it was not requested before.
   * Flag = isRequestedRecentNews
   * 
   * @returns {void}
   * @memberof DataManagerService
   */
  getRecentNews(): void {
    if (!this._isRequestedRecentNews) {
      this._loggerService.debug("Recent News were not requested before, loading news history...");
      this._isRequestedRecentNews = true;
      this.loadNewsHistory(
        null,
        (news) => {
          this._newsMessageService.openAdvertisementNews();
        }
      );
    }
  }

  /**
   * Get recent messages
   * 
   * 1. Check if there is disconnect time stored in cookies. If yes, call loadOfflineMessage & loadOfflinePresence function. (We will check if we really need to load inside the two functions)
   * 2. Only request recent messages if it was not requested before.
   * Flag = _isRequestedRecentMessage
   * 
   * @returns {void}
   * @memberof DataManagerService
   */
  getRecentMessage(): void {
    let disconnectTime = this._localStorageManagerService.getCookiesByKey(TeamNoteLocalStorageKeyConstants.COOKIES.DISCONNECT_TIME);
    if (disconnectTime) {
      // this.loadOfflineMessage(disconnectTime);
      this.loadOfflinePresence(disconnectTime);
    }

    if (!this._isRequestedRecentMessage) {
      this._loggerService.debug("Recent messages were not requested before, loading recent messages...");
      this._isRequestedRecentMessage = true;
      let correlationId = AMQPRoutingKey.CORRELATION_ID.LOAD_RECENT_MESSAGE;
      let routingKey = AMQPRoutingKey[correlationId];
      let headers = {
        'correlation-id': correlationId,
        'reply-to': this.messageQueueName,
        'size': this._teamnoteConfigService.config.WEBCLIENT.AMQP.LOAD_RECENT_MESSAGE_SIZE
      };
      let body = '';

      this._socketService.sendMessage(routingKey, headers, body, correlationId);
    } else {
      //if no need to get recent message, init end here
      this._isInitEnd = true;
    }
  }

  /**
   * Load offline message with disconnect time
   * 
   * Only load if web client is reconnecting
   * 
   * @param {string} disconnectTime - disconnect time of previous session (last active time)
   * @returns {void}
   * @memberof DataManagerService
   */
  loadOfflineMessage(disconnectTime: string): void {
    this._loggerService.debug('[messageQueueName in loadOfflineMessage]' + this.messageQueueName);
    if (this._socketService._isNeedReconnect) {
      this._loggerService.debug("Getting offline messages...");
      let correlationId = AMQPRoutingKey.CORRELATION_ID.LOAD_OFFLINE_MESSAGE;
      let routingKey = AMQPRoutingKey[correlationId];
      let headers = {
        'correlation-id': correlationId,
        'device_token': this._localStorageManagerService.getDeviceToken(),
        'message_time': disconnectTime,
        'reply-to': this.messageQueueName
      };
      let body = '';

      this._socketService.sendMessage(routingKey, headers, body, correlationId);
      this._socketService.setOfflineMsgGot();
      this._chatMessageService.sendFirstMessageInQueue(true);
    }
  }

  /**
   * Load offline presence with disconnet time, existing chat ids
   * 
   * Only load if web client is reconnecting.
   * 
   * @param {string} disconnectTime - disconnect time of previous session (last active time)
   * @returns {void}
   * @memberof DataManagerService
   */
  loadOfflinePresence(disconnectTime: string): void {
    this._loggerService.debug('[presenceQueueName in loadOfflinePresence]' + this.presenceQueueName)
    if (this._socketService._isNeedReconnect) {
      this._loggerService.debug("Getting offline presences...");
      let correlationId = AMQPRoutingKey.CORRELATION_ID.LOAD_OFFLINE_PRESENCE;
      let routingKey = AMQPRoutingKey[correlationId];
      let headers = {
        'correlation-id': correlationId,
        'device_token': this._localStorageManagerService.getDeviceToken(),
        'message_time': disconnectTime,
        'reply-to': this.presenceQueueName
      };

      let allChatIds = this._chatService.getAllChatIds();
      let body = JSON.stringify({
        existing_chat_ids: allChatIds
      });

      this._socketService.sendMessage(routingKey, headers, body, correlationId);
      this._socketService.setOfflinePresenceGot();
    }
  }

  findNumberOfContentMessage(incomingMessage): number {
    let body = JSON.parse(incomingMessage.body);
    let targetMessageTypes = [
      MessageTypeConstant.TEXT,
      MessageTypeConstant.ATTACHMENT,
      MessageTypeConstant.LOCATION,
      MessageTypeConstant.VIDEO_CALL,
      MessageTypeConstant.NEWS,
      MessageTypeConstant.VOTE
    ];
    let contentMessage = _.filter(body, (p) => {
      return _.indexOf(targetMessageTypes, _.toInteger(p.type)) != -1;
    });
    return contentMessage.length;
  }

  /**
   * Load news history under all news channels
   * 
   * @param {Function} [callback] - custom callback function
   * @memberof DataManagerService
   */
  loadNewsHistory(newsCategoryId?: string, callback?: Function): void {
    let allNewsChannelChatIds = _.map(this._chatService.getNewsChannels(), 'chat_id');

    if (allNewsChannelChatIds.length == 0) {
      this._loggerService.warn("Trying to load news history without news channel id, rejected.");
      return;
    }

    this._loggerService.debug("Getting news history...");
    let correlationId = AMQPRoutingKey.CORRELATION_ID.LOAD_HISTORY;
    let routingKey = AMQPRoutingKey[correlationId];
    correlationId += '_NEWS_' + allNewsChannelChatIds.length + '_' + _.now();
    let headers = {
      'correlation-id': correlationId,
      'chat_id': JSON.stringify(allNewsChannelChatIds),
      'reply_to': this.messageQueueName,
      'size': this._teamnoteConfigService.config.WEBCLIENT.AMQP.NEWS_LOAD_HISTORY_SIZE
    };
    let firstNews;
    if (newsCategoryId) {
      headers['news_category_chat_id'] = newsCategoryId;
      firstNews = this._newsMessageService.getEarliestNewsUnderNewsCategory(newsCategoryId);
    } else {
      firstNews = this._newsMessageService.getEarliestNewsUnderNewsChannels(allNewsChannelChatIds);
    }
    if (firstNews) {
      headers['message_time'] = firstNews.timestamp.toString();
    }
    let body = '';

    let loadHistoryCallback = (incomingMessage) => {
      this.receiveMessageCallback(incomingMessage);
      let bodyLength = this.findNumberOfContentMessage(incomingMessage);
      if (callback) {
        callback(bodyLength);
      }
    };

    this._socketService.sendMessage(routingKey, headers, body, correlationId, loadHistoryCallback);
  }


  /**
   * Load chat history of chat
   * 
   * @param {string} chatId - target chat id
   * @param {number} timestamp - local earliest timestamp of messages under chat
   * @param {number} size - target size of messages
   * @param {Function} callback - callback function afer received chat history
   * @returns {void} 
   * @memberof DataManagerService
   */
  loadChatHistory(chatId: string, timestamp: number, size: number, callback?: Function): void {
    if (!chatId) {
      this._loggerService.warn("Trying to load chat history without chat id, rejected.");
      return;
    }
    this._loggerService.debug("Getting chat history..." + chatId);
    let correlationId = 'LOAD_HISTORY_' + chatId + '_' + _.now();
    let routingKey = AMQPRoutingKey.LOAD_HISTORY;
    let headers = {
      'correlation-id': correlationId,
      'reply-to': this.messageQueueName,
      'chat_id': chatId,
      'size': size
    };
    if (timestamp) {
      headers['message_time'] = timestamp
    }
    let body = '';

    let loadHistoryCallback = (incomingMessage) => {
      this.receiveMessageCallback(incomingMessage);
      let bodyLength = this.findNumberOfContentMessage(incomingMessage);
      if (callback) {
        callback(bodyLength);
      }
    };

    this._socketService.sendMessage(routingKey, headers, body, correlationId, loadHistoryCallback);
  }

  /**
   * Subscribe to chat
   * 
   * This will receive user status change under this chat
   * 
   * @param {string} chatId - target chat id
   * @returns {void} 
   * @memberof DataManagerService
   */
  subscribeChat(chatId: string): void {
    if (!chatId) {
      this._loggerService.warn("Trying to subscribe chat without chat id, rejected.");
      return;
    }
    this._loggerService.debug("Subscribing to chat..." + chatId);
    let correlationId = 'SUBSCRIBE_CHAT_' + chatId + '_' + _.now();
    let routingKey = AMQPRoutingKey.SUBSCRIBE_CHAT;
    let headers = {
      'correlation-id': correlationId,
      'reply-to': this.presenceQueueName,
      'chat_id': chatId
    };
    let body = '';
    this._socketService.sendMessage(routingKey, headers, body, correlationId);
  }



  sendMessageToChat(chatId: string, msgBody: Object, msgType: number, whisperingUser?: UserContact, callback?: Function, editingMessageId?: string) {
    if (!chatId || !msgBody || !msgType) {
      this._loggerService.warn('Trying to send message to chat without chat id / message type / message body, rejected');
      return;
    }
    let correlationId = 'SEND_MESSAGE' + chatId + '_' + _.now();
    let routingKey = '/' + chatId;
    let headers = {
      'correlation-id': correlationId,
      'device_token': this._localStorageManagerService.getDeviceToken(),
      'type': !editingMessageId ? msgType : MessageTypeConstant.MESSAGE_UPDATE,
      'priority': 1
    };
    let body = JSON.stringify(msgBody);

    if (whisperingUser) {
      headers['received_by'] = whisperingUser.user_id;
    }

    let newMessage = new Message;
    newMessage.body = body;
    newMessage.sent_by = this._accountManagerService.userId;
    newMessage.type = !editingMessageId ? msgType : MessageTypeConstant.MESSAGE_UPDATE;
    newMessage.timestamp = (_.now() / 1000).toString();
    newMessage.received_by = whisperingUser ? whisperingUser.user_id : "";
    newMessage.correlation_id = correlationId;

    // console.log('newMessage', newMessage);

    if (!editingMessageId) {
      this._chatMessageService.insertOrUpdateChatMessageUnderChat(chatId, newMessage);
    }
    // this._chatMessageService.insertOrUpdateChatMessageUnderChat(chatId, newMessage);
    
    let sendMessageCallback = (incomingMessage) => {
      this.receiveMessageCallback(incomingMessage, false, chatId);

      if (!editingMessageId) {
        if (callback) {
          callback();
        }
      }
    };

    this._chatMessageService.addChatMessageToQueue(correlationId, routingKey, headers, body, sendMessageCallback);
    this._chatMessageService.sendFirstMessageInQueue();

    // this._socketService.sendMessage(routingKey, headers, body, correlationId, sendMessageCallback);

    this._chatMessageService.updateActiveChatRoomMessageSubject();
  }

  /**
   * Get missing user groups / users under root group
   * 
   * Only get if it hasn't try to get before.
   * 
   * Usage: 
   * As we will only try to get missing contacts or user group after receiving user group presence when the user group is under ROOT, we may not have gotten ROOT group before this new presence.
   * We then try to get all missing contact / user group under ROOT after GET_ROSTER has received user group presences.
   * This is to ensure that we have the full contact user / contact user group list.
   * 
   * @returns {void} 
   * @memberof DataManagerService
   */
  tryToGetMissingUserGroupUnderRoot(): void {
    if (this.isGotMissingGroupsUnderRoot) {
      return;
    }
    // Get missing contacts under ROOT
    this._userGroupService.getMissingContactsAndUserGroupContactsUnderUserGroupByUserGroupId(TeamNoteGeneralConstant.ROOT_USER_GROUP_ID);
    this.isGotMissingGroupsUnderRoot = true;

    // Find all child user groups under ROOT recursively
    let allChildGroupIds = this._userGroupService.getAllUserGroupIdsUnderUserGroupByUserGroupId(TeamNoteGeneralConstant.ROOT_USER_GROUP_ID);
    // Get missing contacts under each child group
    _.each(allChildGroupIds, (id) => {
      this._userGroupService.getMissingContactsAndUserGroupContactsUnderUserGroupByUserGroupId(id);
    });
  }

  /**
   * Get user contacts under user group
   * 
   * This generates UserGroup object with "childUserGroup" and "contactsWithoutSelf" under target user group. (With UserGroup object and UserContact object)
   * 
   * @param {string} userGroupId - target user group ID
   * @returns {UserGroup} - parsed user group object
   * @memberof DataManagerService
   */
  getUserContactsUnderUserGroup(userGroupId: string): UserGroup {
    this._loggerService.debug("Getting child user groups and child contacts under user group: " + userGroupId);
    let targetUserGroup = this._userGroupService.getUserGroupByUserGroupId(userGroupId);
    if (!targetUserGroup) {
      this._loggerService.debug("User group not found");
      return;
    }
    targetUserGroup.childUserGroup = [];
    targetUserGroup.contactsWithoutSelf = [];
    _.each(targetUserGroup.children, (childUserGroupId) => {
      targetUserGroup.childUserGroup.push(this._userGroupService.getUserGroupByUserGroupId(childUserGroupId));
    });

    let userIdWithoutSelf = _.without(targetUserGroup.members, this._accountManagerService.userId);

    if (this.allow_self_message) {
      userIdWithoutSelf = targetUserGroup.members;
    }

    _.each(userIdWithoutSelf, (memberUserId) => {
      targetUserGroup.contactsWithoutSelf.push(this._userContactService.getUserContactByUserId(memberUserId));
    });
    
    this._loggerService.debug("No. of child user group: " + targetUserGroup.childUserGroup.length + "\nNo. of child contacts without 'self': " + targetUserGroup.contactsWithoutSelf.length);

    return targetUserGroup;
  }

  /**
   * Get all contact users under user group
   * 
   * This will recuresively get all conatct users under user group
   * 
   * @param {string} userGroupId - target user group ID
   * @returns {UserContact[]} - all UserContact under user group (gotten recursively)
   * @memberof DataManagerService
   */
  getAllContactUsersUnderUserGroup(userGroupId: string): UserContact[] {
    this._loggerService.debug("Getting all contact users under user group: " + userGroupId);
    let targetUserGroup = this._userGroupService.getUserGroupByUserGroupId(userGroupId);
    if (!targetUserGroup) {
      this._loggerService.debug("User group not found");
      return [];
    }
    let allMembers = [];
    // Get UserContact object without current user
    let membersIdWithoutSelf = _.without(targetUserGroup.members, this._accountManagerService.userId);

    if (this.allow_self_message) {
      membersIdWithoutSelf = targetUserGroup.members;
    }

    for (var i in membersIdWithoutSelf) {
      allMembers.push(this._userContactService.getUserContactByUserId(membersIdWithoutSelf[i]));
    }

    // Recursively get all contact users under its childrens
    for (var j in targetUserGroup.children) {
      let childMembers = this.getAllContactUsersUnderUserGroup(targetUserGroup.children[j]);
      allMembers = _.union(allMembers, childMembers);
    }
    this._loggerService.debug("User group: " + userGroupId + " \nNo. of contact users: " + allMembers.length);
    return allMembers;
  }

  /* for starred messages page usage, check for when should to get data */
  updateIsChatLoaded(isLoaded: boolean): void {
    this.isChatLoaded = isLoaded;
    this.isChatLoaded$.next(this.isChatLoaded);
  }
}
